import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { ConversationParticipation } from '@provision/entity-types';
import { useMemo } from 'react';
import {
  ConversationParticipantItem,
  isClientDeleted,
  useConversationParticipants,
} from 'src/graphql';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { usePlatformUserIds } from 'src/graphql/entities/platform-user';
import { ConditionInput } from 'src/graphql/types';
import { toConversationChannelType, toParticipantEntity } from './conversation-enum-values';

const EXCLUDE_BOTS_FILTER: ConditionInput[] = [
  {
    attribute: 'participantEntity',
    operator: '!=',
    value: ConversationParticipantEntity.Bot,
  },
];

const ACTIVE_PLATFORM_USERS_FILTER: ConditionInput[] = [
  {
    attribute: 'isActive',
    operator: '=',
    value: 'true',
  },
];

export interface ParticipantPillItem {
  displayName: string;
  entity: ConversationParticipantEntity;
  participantIdentity: string;
  conversationParticipantId: string;
  participantPartnerId?: number;
  entityPartnerId?: number;
  clientPrimaryEmail?: string;
  platformUserEmail?: string;
  isUnidentified: boolean;
  isFollower: boolean;
  isDeactivatedAgent: boolean;
  isDeletedClient: boolean;
  /**
   * Raw escape hatches: the client/participant modals and the follower toggle
   * mutate whole entities, so they need the records themselves, not mapped fields.
   */
  participant: ConversationParticipantItem;
  rawEmailParticipation?: ConversationParticipation;
}

export interface UseConversationParticipantPillsResult {
  pills: ParticipantPillItem[];
  isLoading: boolean;
  refetchParticipations: ReturnType<typeof useConversationParticipationRecords>['refetch'];
}

const getEntityPartnerId = (
  participant: ConversationParticipantItem,
  entity: ConversationParticipantEntity,
): number | undefined => {
  switch (entity) {
    case ConversationParticipantEntity.Client:
      return participant.client?.partnerId;
    case ConversationParticipantEntity.PlatformUser:
      return participant.platformUser?.partnerId;
    case ConversationParticipantEntity.Rsm:
      return participant.rsmEmail?.partnerId;
    default:
      return undefined;
  }
};

const getDisplayName = (
  participant: ConversationParticipantItem,
  entity: ConversationParticipantEntity,
): string => {
  if (entity === ConversationParticipantEntity.Unknown) {
    return (
      participant.participantIdentity ||
      participant.conversationChannels.find((channel) => !!channel.externalIdentifier)
        ?.externalIdentifier ||
      ''
    );
  }

  return (
    participant.platformUser?.fullName ||
    participant.client?.fullName ||
    participant.rsmEmail?.name ||
    ''
  );
};

const findEmailParticipation = (
  participant: ConversationParticipantItem,
  participations: ConversationParticipation[],
) =>
  participations.find((participation) =>
    participant.conversationChannels.some(
      (channel) =>
        channel.id === participation.channel.id &&
        toConversationChannelType(channel.conversationChannelTypeId) ===
          ConversationChannelType.Email,
    ),
  );

export const useConversationParticipantPills = ({
  conversationId,
  partnerId,
}: {
  conversationId: string;
  partnerId: number;
}): UseConversationParticipantPillsResult => {
  const { conversationParticipations: participations, refetch: refetchParticipations } =
    useConversationParticipationRecords({
      conversationId,
      includeDeleted: true,
    });

  const participantIds = useMemo(
    () =>
      participations.map((participation) => participation.channel.conversationParticipantId),
    [participations],
  );

  const { conversationParticipants, loading: isLoading } = useConversationParticipants(
    { participantIds, filter: EXCLUDE_BOTS_FILTER },
    { skip: !participations.length },
  );

  const { data: activePlatformUserIds } = usePlatformUserIds({
    partnerId,
    filters: ACTIVE_PLATFORM_USERS_FILTER,
  });

  const pills = useMemo(() => {
    const activeParticipationChannelIds = new Set(
      participations
        .filter((participation) => participation.isActive)
        .map((participation) => participation.channel.id),
    );

    const seenParticipantIdentities = new Set<string>();
    const unidentified: ParticipantPillItem[] = [];
    const identified: ParticipantPillItem[] = [];

    for (const participant of conversationParticipants) {
      const hasActiveChannel = participant.conversationChannels.some((channel) =>
        activeParticipationChannelIds.has(channel.id),
      );

      if (!hasActiveChannel) {
        continue;
      }

      if (seenParticipantIdentities.has(participant.participantIdentity)) {
        continue;
      }
      seenParticipantIdentities.add(participant.participantIdentity);

      const entity = toParticipantEntity(participant.participantEntity);
      const isAgent = entity === ConversationParticipantEntity.PlatformUser;
      const isDeactivatedAgent =
        isAgent && !activePlatformUserIds.includes(participant.participantIdentity);
      const emailParticipation = isAgent
        ? findEmailParticipation(participant, participations)
        : undefined;
      const isFollower = isAgent && !!emailParticipation?.isActive;

      const item: ParticipantPillItem = {
        displayName: getDisplayName(participant, entity),
        entity,
        participantIdentity: participant.participantIdentity,
        conversationParticipantId: participant.conversationParticipantId,
        participantPartnerId: participant.partnerId,
        entityPartnerId: getEntityPartnerId(participant, entity),
        clientPrimaryEmail: participant.client?.primaryEmail,
        platformUserEmail: participant.platformUser?.email,
        isUnidentified: entity === ConversationParticipantEntity.Unknown,
        isFollower,
        isDeactivatedAgent,
        isDeletedClient: isClientDeleted(participant.client),
        participant,
        rawEmailParticipation: isFollower ? emailParticipation : undefined,
      };

      if (item.isUnidentified) {
        unidentified.push(item);
      } else {
        identified.push(item);
      }
    }

    return [...unidentified, ...identified];
  }, [conversationParticipants, participations, activePlatformUserIds]);

  return { pills, isLoading, refetchParticipations };
};
